import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, useSearchParams, Link } from "react-router-dom";
import { ArrowLeft, Download, Search, Eye, TrendingUp, Users, FileCheck, DollarSign, Calendar, ShoppingBag, BarChart3, ExternalLink, Home, ArrowUp, ArrowDown, ArrowUpDown, Instagram, Youtube, Music2, Globe, RefreshCw, Copy } from "lucide-react";
import * as XLSX from "xlsx";
import { api } from "../../lib/api";
import { useAuth } from "../../lib/auth";
import { ImpersonationBanner } from "../../lib/impersonate";
import { useSettings } from "../../lib/settings";
import WishlinkLogo from "../../components/WishlinkLogo";
import { Skeleton, EmptyState, ProgressRing, StackedBar, StatusPill, Avatar, BrandLogo, Pagination, DeltaBadge, resolveStatus, ClearFiltersButton } from "../../components/common";
import { aggregate, formatINRAbbrev, formatNumberAbbrev, formatByType } from "../../lib/format";
import { toast } from "sonner";

const ICON_MAP = { eye: Eye, "trending-up": TrendingUp, users: Users, "file-check": FileCheck, "dollar-sign": DollarSign, calendar: Calendar, "shopping-bag": ShoppingBag, "bar-chart-3": BarChart3 };

// ---- helpers ----
const NUMERIC_RE = /^-?[\d,.]+%?$/;
const toNumber = (v) => {
  if (v === null || v === undefined || v === "") return NaN;
  const s = String(v).replace(/[,₹$\s]/g, "").replace(/%$/, "");
  const n = Number(s);
  return Number.isFinite(n) ? n : NaN;
};
const isNumericColumn = (rows, col) => {
  if (!rows.length) return false;
  let numeric = 0, total = 0;
  for (const r of rows) {
    const v = r[col];
    if (v === null || v === undefined || v === "") continue;
    total += 1;
    if (NUMERIC_RE.test(String(v).trim()) && !isNaN(toNumber(v))) numeric += 1;
  }
  return total > 0 && numeric / total >= 0.7;
};
const detectPlatform = (url) => {
  if (!url) return null;
  const u = String(url).toLowerCase();
  if (u.includes("instagram.")) return "instagram";
  if (u.includes("youtube.") || u.includes("youtu.be")) return "youtube";
  if (u.includes("tiktok.")) return "tiktok";
  return "web";
};
const PlatformIcon = ({ platform, size = 13 }) => {
  const map = { instagram: Instagram, youtube: Youtube, tiktok: Music2, web: Globe };
  const I = map[platform] || Globe;
  const colors = { instagram: "#e1306c", youtube: "#ff0000", tiktok: "#000000", web: "#6b7280" };
  return <I size={size} style={{ color: colors[platform] || "#6b7280" }} strokeWidth={2} />;
};

function SortableTh({ label, sortKey, sort, setSort, numeric = false, align = "left" }) {
  const active = sort.key === sortKey;
  // 3-state cycle:
  //   numeric:   off → DESC → ASC → off
  //   text:      off → ASC  → DESC → off
  const toggle = () => {
    const firstDir = numeric ? "desc" : "asc";
    const secondDir = numeric ? "asc" : "desc";
    if (!active) setSort({ key: sortKey, dir: firstDir, numeric });
    else if (sort.dir === firstDir) setSort({ key: sortKey, dir: secondDir, numeric });
    else setSort({ key: null, dir: null, numeric: false });
  };
  const Icon = !active ? ArrowUpDown : sort.dir === "asc" ? ArrowUp : ArrowDown;
  return (
    <th style={{ textAlign: align }}>
      <button
        type="button"
        onClick={toggle}
        className={`inline-flex items-center gap-1.5 transition-colors ${active ? "text-[var(--brand)]" : "text-inherit hover:text-[var(--text-primary)]"}`}
        data-testid={`th-sort-${sortKey}`}
        title={`Sort by ${label}`}
      >
        <span>{label}</span>
        <Icon size={12} className={active ? "opacity-100" : "opacity-40"} strokeWidth={2.4} />
      </button>
    </th>
  );
}

function applySort(rows, sort) {
  if (!sort?.key) return rows;
  const { key, dir, numeric } = sort;
  const arr = [...rows];
  arr.sort((a, b) => {
    const av = a[key], bv = b[key];
    if (numeric) {
      const an = toNumber(av), bn = toNumber(bv);
      const ax = isNaN(an) ? -Infinity : an;
      const bx = isNaN(bn) ? -Infinity : bn;
      return dir === "asc" ? ax - bx : bx - ax;
    }
    return dir === "asc"
      ? String(av || "").localeCompare(String(bv || ""))
      : String(bv || "").localeCompare(String(av || ""));
  });
  return arr;
}

function ImgWithFallback({ src, alt, fallback, className, draggable = false }) {
  const [broken, setBroken] = React.useState(false);
  React.useEffect(() => { setBroken(false); }, [src]);
  if (broken || !src) return fallback;
  return <img src={src} alt={alt} className={className} draggable={draggable} onError={() => setBroken(true)} />;
}

function FreshnessLabel({ fetchedAt }) {
  // Live-updating "fetched 4 minutes ago" label. Re-renders every 30s so the
  // copy stays accurate without forcing a parent re-fetch.
  const [now, setNow] = React.useState(() => Date.now());
  React.useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 30000);
    return () => clearInterval(t);
  }, []);
  if (!fetchedAt) return null;
  const ts = new Date(fetchedAt).getTime();
  if (!Number.isFinite(ts)) return null;
  const seconds = Math.max(0, Math.floor((now - ts) / 1000));
  let label;
  if (seconds < 10) label = "just now";
  else if (seconds < 60) label = `${seconds}s ago`;
  else if (seconds < 3600) label = `${Math.floor(seconds / 60)}m ago`;
  else label = `${Math.floor(seconds / 3600)}h ago`;
  // Cache TTL is 5min — show a soft warning past that since the next view will refresh.
  const stale = seconds > 300;
  return (
    <span className="inline-flex items-center gap-1" title={new Date(fetchedAt).toLocaleString()}>
      <span className={`w-1.5 h-1.5 rounded-full ${stale ? "bg-amber-500" : "bg-emerald-500"}`} />
      Data fetched <strong className="text-[var(--text-primary)] font-medium">{label}</strong>
    </span>
  );
}

export default function TrackingPage() {
  const { id } = useParams();
  const { user } = useAuth();
  const { settings } = useSettings();
  const [params, setParams] = useSearchParams();
  const tab = params.get("tab") || "overview";
  const setTab = (t) => setParams({ tab: t });
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const nav = useNavigate();

  // Endpoint routing: a *true* admin (non-impersonated) hits the admin endpoint
  // which can read ANY campaign. An impersonated admin holds a brand token, so
  // they must hit the brand endpoint (which scopes to their assigned campaigns).
  const isRealAdmin = user?.type === "admin" && !user?.impersonated_by;
  // UI gate: refresh strip is shown to anyone with admin context (real OR impersonated).
  const showAdminTools = user?.type === "admin" || !!user?.impersonated_by;
  const [refreshing, setRefreshing] = useState(false);

  const load = async ({ refresh = false } = {}) => {
    if (refresh) setRefreshing(true);
    else setLoading(true);
    setErr(null);
    try {
      const base = isRealAdmin ? `/campaigns/${id}/tracking` : `/brand/campaigns/${id}/tracking`;
      const url = refresh ? `${base}?refresh=1` : base;
      const { data } = await api.get(url);
      setData(data);
      if (refresh) toast.success("Tracker refreshed");
    } catch (ex) {
      setErr(ex?.response?.data?.detail || "Failed to load tracker");
      if (refresh) toast.error("Refresh failed");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [id, isRealAdmin]);

  if (loading) return <div className="space-y-3">{Array.from({ length: 6 }).map((_, i) => <Skeleton key={i} className="h-20" />)}</div>;
  if (err) return <EmptyState title="Cannot view tracker" subtitle={err} />;
  if (!data) return null;

  const c = data.campaign;
  const cfg = data.config || {};
  const sheet = data.sheet || {};
  const sheetErr = sheet?.error;
  const overviewSlots = (cfg.overview_config?.slots || []).filter((s) => s.enabled);
  const statusMappings = cfg.status_mappings || [];

  // Resolve which rowset an Overview slot should aggregate over, honoring its
  // `status_filter_mode`:
  //   - "inherit" (default): the rows already filtered by the backend per the
  //     tab's `allowed_statuses` config — i.e. `sheet.tabs[tab].rows`.
  //   - "all": ignore the tab filter; aggregate the full population. The
  //     backend exposes this as `_all_rows` on every Creators/Content tab.
  //   - "custom": client-side filter `_all_rows` by the slot's own column +
  //     allow-list. The slot can name its own filter column via
  //     `slot.status_filter_column`; if absent, falls back to the tab's
  //     configured column / autodetected status column.
  const rowsForSlot = (tabKey, slot) => {
    const tab = sheet?.tabs?.[tabKey];
    if (!tab) return [];
    const mode = slot?.status_filter_mode || "inherit";
    const all = tab._all_rows || tab.rows || [];
    if (mode === "all") return all;
    if (mode === "custom") {
      const allowed = slot.status_filter_custom || [];
      if (!allowed.length) return all; // empty custom list ≡ no filter
      const headers = tab.headers || [];
      // Per-slot column wins; else fall back to tab metadata / autodetect.
      const col = slot.status_filter_column
        || tab.status_column
        || headers.find((h) => /status|opt[\s\-_]?in/i.test(String(h)));
      if (!col || !headers.includes(col)) return all;
      const set = new Set(allowed.map(String));
      return all.filter((r) => set.has(String(r[col] ?? "")));
    }
    // inherit
    return tab.rows || [];
  };

  const homeBack = () => isRealAdmin ? nav(`/admin/campaigns/${id}/config`) : nav("/campaigns");

  const downloadReport = () => {
    try {
      const wb = XLSX.utils.book_new();
      // SUMMARY — clean "Metric | Value" table
      const summaryRows = [["Metric", "Value"]];
      summaryRows.push(["Campaign Name", c.campaign_name || ""]);
      summaryRows.push(["Campaign ID", c.campaign_id || ""]);
      summaryRows.push(["Brand", data?.brand?.name || ""]);
      summaryRows.push(["Status", c.status || ""]);
      if (c.tracker_values?.month) summaryRows.push(["Month", c.tracker_values.month]);
      if (c.tracker_values?.date)  summaryRows.push(["Date", c.tracker_values.date]);
      if (c.tracker_values?.live_date) summaryRows.push(["Live Date", c.tracker_values.live_date]);
      // Overview slots — flat metric | value rows (no format/aggregation clutter)
      overviewSlots.forEach((s) => {
        const tabRows = rowsForSlot(s.tab, s);
        const v = aggregate(tabRows, s.column, s.aggregation, s.filter_value);
        summaryRows.push([s.label, formatByType(v, s.format)]);
      });
      XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(summaryRows), "Summary");
      // CREATORS — raw rows from the creators tab (not campaign tab!)
      if (sheet?.tabs?.creators) {
        const { headers, rows } = sheet.tabs.creators;
        const aoa = [headers, ...rows.map((r) => headers.map((h) => r[h] ?? ""))];
        XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(aoa), "Creators");
      }
      // CONTENT — raw rows from the content tab
      if (sheet?.tabs?.content) {
        const { headers, rows } = sheet.tabs.content;
        const aoa = [headers, ...rows.map((r) => headers.map((h) => r[h] ?? ""))];
        XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(aoa), "Content");
      }
      // Filename: campaign name (sanitized) + campaign id + date
      const safeName = (c.campaign_name || "campaign").replace(/[^a-zA-Z0-9-]+/g, "_").replace(/^_+|_+$/g, "").slice(0, 60);
      const dateStr = new Date().toISOString().slice(0, 10);
      XLSX.writeFile(wb, `${safeName}_${c.campaign_id}_${dateStr}.xlsx`);
    } catch (e) { toast.error("Export failed"); }
  };

  return (
    <div className="min-h-screen brand-no-image-interactions" style={{ background: "var(--bg-page)" }} data-testid="tracking-page">
      <ImpersonationBanner user={user} />
      {/* Top bar — warm beige gradient, matches brand portal & admin sidebar treatment */}
      <div
        className="border-b border-[var(--border-default)]"
        style={{ background: "linear-gradient(180deg, var(--bg-tint) 0%, var(--bg-page) 100%)" }}
      >
        <div className="max-w-[1400px] mx-auto px-6 sm:px-10 h-24 flex items-center justify-between gap-3">
          <button onClick={homeBack} className="wl-btn-ghost inline-flex items-center gap-1.5 flex-shrink-0" data-testid="tracker-home"><Home size={14} /> <span className="hidden sm:inline">{isRealAdmin ? "Back to configurator" : "All campaigns"}</span></button>
          <div className="text-center min-w-0 px-3">
            <WishlinkLogo
              className="h-9 w-auto max-w-[160px] mx-auto object-contain block"
              fallbackClassName="font-display text-2xl font-semibold tracking-tight text-[var(--brand)]"
            />
            <div className="text-[10px] uppercase tracking-[0.16em] font-semibold mt-1" style={{ color: "var(--brand)" }}>Tracker</div>
          </div>
          <button onClick={downloadReport} className="wl-btn-secondary inline-flex items-center gap-1.5 flex-shrink-0" disabled={!sheet?.tabs} data-testid="download-report-btn"><Download size={14} /> <span className="hidden sm:inline">Download report</span></button>
        </div>
      </div>

      {/* Admin-only data freshness strip */}
      {showAdminTools && sheet?.tabs && (
        <div className="max-w-[1400px] mx-auto px-6 sm:px-10 -mb-6 pt-4 flex items-center justify-end gap-3 text-xs text-[var(--text-secondary)]" data-testid="tracker-refresh-strip">
          <FreshnessLabel fetchedAt={sheet.fetched_at} />
          <button
            onClick={() => load({ refresh: true })}
            disabled={refreshing}
            className="wl-btn-ghost inline-flex items-center gap-1.5 text-xs disabled:opacity-50"
            data-testid="tracker-refresh-btn"
            title="Force a fresh fetch from the Google Sheet (bypasses the 5-minute cache)"
          >
            <RefreshCw size={12} className={refreshing ? "animate-spin" : ""} />
            {refreshing ? "Refreshing…" : "Refresh"}
          </button>
        </div>
      )}

      <div className="max-w-[1400px] mx-auto px-10 py-12">
        {/* Brand identity — hero */}
        <div className="flex items-center justify-center gap-8 mb-6" data-testid={`brand-identity-${c.campaign_type}`}>
          {c.partner_1_logo_url
            ? <div className="h-24 w-24 rounded-2xl bg-white border border-[var(--border-default)] p-3 flex items-center justify-center" style={{ boxShadow: "0 8px 24px -12px rgba(232,97,58,0.18)" }}>
                <ImgWithFallback
                  src={c.partner_1_logo_url}
                  alt={c.partner_1_name}
                  className="max-h-full max-w-full object-contain"
                  fallback={<BrandLogo name={c.partner_1_name || data.brand?.name} size={84} />}
                />
              </div>
            : <BrandLogo name={c.partner_1_name || data.brand?.name} size={96} />}

          {c.campaign_type === "ecom_x_brand" && (
            <>
              <span className="text-[var(--brand)] text-3xl font-light font-display">×</span>
              {c.partner_2_logo_url
                ? <div className="h-24 w-24 rounded-2xl bg-white border border-[var(--border-default)] p-3 flex items-center justify-center" style={{ boxShadow: "0 8px 24px -12px rgba(232,97,58,0.18)" }}>
                    <ImgWithFallback
                      src={c.partner_2_logo_url}
                      alt={c.partner_2_name}
                      className="max-h-full max-w-full object-contain"
                      fallback={<BrandLogo name={c.partner_2_name || "P2"} size={84} />}
                    />
                  </div>
                : <BrandLogo name={c.partner_2_name || "P2"} size={96} />}
            </>
          )}

          {c.campaign_type === "ecom_x_category" && c.category_name && (
            <>
              <span className="text-[var(--brand)] text-3xl font-light font-display">×</span>
              <span className="font-display text-4xl font-semibold tracking-tight text-[var(--text-primary)]" data-testid="tracker-category-name">{c.category_name}</span>
            </>
          )}

          {/* brand_only: only the single partner logo, no × */}
        </div>
        <div className="text-center mb-1">
          <h1 className="font-display text-3xl font-semibold tracking-tight">{c.campaign_name}</h1>
          <div className="text-xs text-[var(--text-secondary)] mt-1">{c.campaign_id}{c.tracker_values?.live_date ? ` · Live ${c.tracker_values.live_date}` : ""}</div>
        </div>

        {/* Tabs — admins can hide Creator Summary / Content Detail per campaign via
            cfg.{creators,content}_config.tab_visible. Default = visible. Overview is
            always shown. Custom tabs (cfg.tracker_config.custom_tabs[]) are appended
            after the fixed three, in their configured order. If the user is currently
            on a hidden/removed tab, fall back to overview. */}
        {(() => {
          const creatorsVisible = cfg?.creators_config?.tab_visible !== false;
          const contentVisible = cfg?.content_config?.tab_visible !== false;
          const customTabs = (cfg?.tracker_config?.custom_tabs || [])
            .filter((t) => t.tab_visible !== false && t.sheet_tab_name)
            .sort((a, b) => (a.order || 0) - (b.order || 0));
          const allTabs = [
            { k: "overview", l: "Overview", show: true },
            { k: "creators", l: "Creator Summary", show: creatorsVisible },
            { k: "content", l: "Content Detail", show: contentVisible },
            ...customTabs.map((t) => ({ k: `custom_${t.id}`, l: t.label || "Custom", show: true })),
          ].filter((t) => t.show);
          if (!allTabs.find((t) => t.k === tab)) {
            setTimeout(() => setTab("overview"), 0);
          }
          return (
            <div className="border-b border-[var(--border-default)] mt-8 mb-8 flex gap-8 justify-center text-sm flex-wrap">
              {allTabs.map((t) => (
                <button key={t.k} onClick={() => setTab(t.k)}
                  className={`pb-3 border-b-2 transition-colors ${tab === t.k ? "border-[var(--brand)] text-[var(--brand)] font-medium" : "border-transparent text-[var(--text-secondary)] hover:text-[var(--text-primary)]"}`}
                  data-testid={`tracker-tab-${t.k}`}>
                  {t.l}
                </button>
              ))}
            </div>
          );
        })()}

        {sheetErr && (
          <div className="wl-card p-6 text-sm" data-testid="sheet-error">
            <div className="font-semibold text-[#991b1b] mb-1">
              {sheet?.error_kind === "access_denied" ? "Sheet not shared with the portal" : "Data temporarily unavailable"}
            </div>
            <div className="text-[var(--text-secondary)]">{sheetErr}</div>
            {sheet?.sa_email && (
              <div className="mt-3 flex items-center gap-2 flex-wrap">
                <code className="font-mono text-xs px-2 py-1.5 rounded bg-[var(--bg-surface-warm)] border border-[var(--border-default)]" data-testid="sheet-error-sa-email">{sheet.sa_email}</code>
                <button
                  onClick={async () => { try { await navigator.clipboard.writeText(sheet.sa_email); toast.success("Copied"); } catch { toast.error("Couldn't copy"); } }}
                  className="wl-btn-secondary text-xs inline-flex items-center gap-1"
                  data-testid="sheet-error-copy"
                ><Copy size={11} /> Copy email</button>
                <span className="text-[11px] text-[var(--text-secondary)]">Share the campaign sheet as Viewer with this email.</span>
              </div>
            )}
          </div>
        )}

        {!sheetErr && tab === "overview" && (
          overviewSlots.length === 0
            ? <EmptyState title="Overview not configured for this campaign" subtitle={showAdminTools ? "Configure card slots in the dashboard configurator." : "Please contact your Wishlink representative."} />
            : <OverviewTab slots={overviewSlots} sheet={sheet} statusMappings={statusMappings} rowsForSlot={rowsForSlot} />
        )}
        {!sheetErr && tab === "creators" && <CreatorsTab cfg={cfg.creators_config} rows={sheet?.tabs?.creators?.rows || []} headers={sheet?.tabs?.creators?.headers || []} statusMappings={statusMappings} />}
        {!sheetErr && tab === "content" && <ContentTab cfg={cfg.content_config} rows={sheet?.tabs?.content?.rows || []} headers={sheet?.tabs?.content?.headers || []} statusMappings={statusMappings} />}
        {!sheetErr && tab.startsWith("custom_") && (() => {
          const ct = (cfg?.tracker_config?.custom_tabs || []).find((t) => `custom_${t.id}` === tab);
          if (!ct) return null;
          const tabData = sheet?.tabs?.[tab] || {};
          return (
            <CreatorsTab
              cfg={{ columns: ct.columns || [], title: ct.label || "Custom" }}
              rows={tabData.rows || []}
              headers={tabData.headers || []}
              statusMappings={statusMappings}
            />
          );
        })()}
      </div>
    </div>
  );
}

function OverviewTab({ slots, sheet, statusMappings, rowsForSlot }) {
  const heroes = slots.filter((s) => (s.row || 1) === 1);
  const secondaries = slots.filter((s) => (s.row || 1) === 2);
  const thirds = slots.filter((s) => (s.row || 1) === 3);

  return (
    <div className="space-y-6" data-testid="overview-tab">
      {heroes.length > 0 && <SlotRow slots={heroes} sheet={sheet} variant="hero" statusMappings={statusMappings} rowsForSlot={rowsForSlot} />}
      {secondaries.length > 0 && <SlotRow slots={secondaries} sheet={sheet} variant="secondary" statusMappings={statusMappings} rowsForSlot={rowsForSlot} />}
      {thirds.length > 0 && <SlotRow slots={thirds} sheet={sheet} variant="secondary" statusMappings={statusMappings} rowsForSlot={rowsForSlot} />}
    </div>
  );
}

function SlotRow({ slots, sheet, variant, statusMappings, rowsForSlot }) {
  return (
    <div className={`grid ${slots.length === 1 ? "grid-cols-1" : "grid-cols-2"} gap-5`}>
      {slots.map((s) => <SlotCard key={s.id} slot={s} sheet={sheet} variant={variant} statusMappings={statusMappings} rowsForSlot={rowsForSlot} />)}
    </div>
  );
}

function SlotCard({ slot, sheet, variant, statusMappings, rowsForSlot }) {
  // tabRows reflects the slot's status_filter_mode (inherit/all/custom).
  // For Creators/Content tabs this is what aggregates against; for other tabs
  // (campaign / custom) the helper just returns the raw rows.
  const tabRows = rowsForSlot ? rowsForSlot(slot.tab, slot) : (sheet?.tabs?.[slot.tab]?.rows || []);
  // static_value (when set in slot config) overrides any sheet aggregation — useful for demo
  // values, hardcoded targets or metrics not present in the sheet.
  const hasStatic = slot.static_value !== undefined && slot.static_value !== null && slot.static_value !== "";
  const value = hasStatic ? slot.static_value : aggregate(tabRows, slot.column, slot.aggregation, slot.filter_value);
  const Icon = ICON_MAP[slot.icon] || BarChart3;
  // Comparison metric delta % (config field: comparison_column).
  // Only render the delta badge when the comparison column is set AND the change
  // is meaningfully non-zero — avoids "+0%" noise on slots where current and
  // comparison values are equal (e.g. first day of campaign).
  let delta = null;
  if (slot.comparison_column && !hasStatic) {
    const compare = aggregate(tabRows, slot.comparison_column, slot.aggregation, slot.filter_value);
    if (compare && Number(compare) !== 0) {
      const computed = ((Number(value) - Number(compare)) / Number(compare)) * 100;
      if (Math.abs(computed) >= 0.5) delta = computed;
    }
  }
  // Progress ring percentage:
  // 1) If slot.progress_target_column is set → numerator / aggregate(target_column on target_tab) * 100
  //    The denominator can come from a different tab (e.g. campaign tab "posts_target" column).
  //    Denominator IGNORES slot.filter_value so the % means "of all expected".
  //    If the configured denominator column is missing / zero / unparseable, we fall back to
  //    the static target (if set) so a typo'd column name doesn't silently break the widget.
  // 2) Else if slot.progress_target is set → (value / progress_target) * 100
  // 3) Else if format is percentage → use value directly
  // 4) Else fall back to (value / row_count) * 100
  let ringPct = 0;
  let progressDenom = slot.progress_target ?? "";
  let denomFromColumn = null;
  if (slot.progress_target_column) {
    // Denominator = "of all expected" — explicitly the unfiltered population
    // on the target tab. Status filters control the numerator, not the goal.
    const denomTab = sheet?.tabs?.[slot.progress_target_tab || slot.tab];
    const denomTabRows = (denomTab && (denomTab._all_rows || denomTab.rows)) || [];
    const denomAgg = slot.progress_target_aggregation || "SUM";
    const computed = aggregate(denomTabRows, slot.progress_target_column, denomAgg, "");
    if (Number(computed) > 0) denomFromColumn = Number(computed);
  }
  if (denomFromColumn !== null) {
    progressDenom = denomFromColumn;
    ringPct = (Number(value) / denomFromColumn) * 100;
  } else if (slot.progress_target) {
    ringPct = Number(slot.progress_target) ? (Number(value) / Number(slot.progress_target)) * 100 : 0;
  } else if (slot.format === "percentage") {
    ringPct = Number(value);
  } else if (tabRows.length) {
    ringPct = (Number(value) / tabRows.length) * 100;
  }

  // Helper: keep 1 decimal of precision when % < 10 (avoids "1.5%" rounding to "2%"),
  // round to whole numbers above that.
  const fmtPctPrecise = (n) => {
    const v = Math.max(0, Math.min(100, Number(n) || 0));
    return v < 10 && v !== Math.floor(v) ? `${(Math.round(v * 10) / 10)}%` : `${Math.round(v)}%`;
  };

  // When the slot has format=percentage AND a denominator is in play (column or static
  // target), the headline number IS the computed ratio — not the raw numerator. This
  // matches what the ring visualizes, so big number + ring always agree.
  const showRatioAsHeadline =
    slot.format === "percentage" && (slot.progress_target_column || slot.progress_target);
  const formatted = showRatioAsHeadline ? fmtPctPrecise(ringPct) : formatByType(value, slot.format);

  const replacements = {
    "{value}": formatted,
    "{value_abbreviated}": (slot.format || "").startsWith("currency")
      ? formatINRAbbrev(value)
      : formatNumberAbbrev(value),
    "{row_count}": tabRows.length,
    "{column}": slot.column,
    "{progress_target}": progressDenom,
    "{progress_pct}": fmtPctPrecise(ringPct),
    "{delta}": delta !== null ? `${delta >= 0 ? "+" : ""}${Math.round(delta * 10) / 10}%` : "",
  };
  const renderSubtext = (line) => Object.entries(replacements).reduce((s, [k, v]) => s.split(k).join(String(v)), line);

  const isHero = variant === "hero";
  return (
    <div
      className="wl-card-solid p-7 transition-all hover:shadow-[0_12px_32px_-16px_rgba(232,97,58,0.22)] hover:-translate-y-0.5"
      data-testid={`slot-card-${slot.id}`}
    >
      <div className="flex items-center gap-3 mb-5">
        <div className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0" style={{ background: "var(--brand-tint)", color: "var(--brand)" }}>
          <Icon size={18} strokeWidth={2} />
        </div>
        <div className="wl-label">{slot.label}</div>
        {delta !== null && <div className="ml-auto"><DeltaBadge value={delta} /></div>}
      </div>
      {/* Fixed structure: items-start anchors the big number to the same vertical
          position across every card, and we always render MAX_SUBTEXT subtext rows
          (placeholder &nbsp; for empty slots) so card heights stay consistent. */}
      <div className="flex items-start gap-6">
        <div className="flex-1 min-w-0">
          <div className="font-display text-[44px] leading-[1.05] font-semibold tracking-tight text-[var(--text-primary)]" data-testid={`slot-value-${slot.id}`}>
            {formatted}
          </div>
          <div className="mt-3 space-y-2">
            {[0, 1].map((i) => {
              const line = (slot.subtext || [])[i];
              return (
                <div key={i} className="text-[13px] text-[var(--text-secondary)] leading-relaxed min-h-[1.25rem]">
                  {line ? renderSubtext(line) : "\u00A0"}
                </div>
              );
            })}
          </div>
        </div>
        {!isHero && slot.viz === "progress_ring" && (
          <div className="shrink-0 self-center"><ProgressRing value={ringPct} size={104} stroke={10} /></div>
        )}
        {!isHero && slot.viz === "stacked_bar" && (() => {
          const colMap = {};
          tabRows.forEach((r) => { const k = r[slot.column] || "—"; colMap[k] = (colMap[k] || 0) + 1; });
          const isStatusCol = /status|opt[\s\-_]?in/i.test(slot.column || "");
          const colorByStatus = { green: "#22c55e", amber: "#f59e0b", red: "#ef4444", blue: "#3b82f6", gray: "#94a3b8" };
          const palette = ["#e8613a", "#f59e0b", "#10b981", "#3b82f6", "#8b5cf6", "#94a3b8"];
          const segs = Object.entries(colMap).map(([k, v], i) => {
            if (isStatusCol) {
              const r = resolveStatus(k, statusMappings) || { label: k, color: "gray" };
              return { label: r.label, value: v, color: colorByStatus[r.color] || palette[i % palette.length] };
            }
            return { label: k, value: v, color: palette[i % palette.length] };
          });
          return <div className="w-56 shrink-0 self-center"><StackedBar segments={segs} /></div>;
        })()}
      </div>
    </div>
  );
}

function CreatorsTab({ cfg, rows, headers, statusMappings }) {
  const [page, setPage] = useState(1);
  const [q, setQ] = useState("");
  const [statusF, setStatusF] = useState("");
  const [sort, setSort] = useState({ key: null, dir: null, numeric: false });
  const PAGE = 10;

  const columns = useMemo(() => {
    const cols = (cfg?.columns || []).filter((c) => c.visible).sort((a, b) => (a.order || 0) - (b.order || 0));
    if (cols.length) return cols;
    return headers.map((h) => ({ column: h, display_name: h, format: "text", align: "left", visible: true }));
  }, [cfg, headers]);

  // Pre-compute which columns are numeric → sort numerically + right-align
  const numericMap = useMemo(() => {
    const m = {};
    columns.forEach((c) => { m[c.column] = ["number", "currency", "percentage"].includes(c.format) || isNumericColumn(rows, c.column); });
    return m;
  }, [columns, rows]);

  const statusCol = headers.find((h) => /status/i.test(h));
  const statusOptions = useMemo(() => statusCol ? [...new Set(rows.map((r) => r[statusCol]).filter(Boolean))] : [], [rows, statusCol]);

  const filtered = useMemo(() => {
    const arr = rows.filter((r) => {
      if (q && !Object.values(r).some((v) => String(v).toLowerCase().includes(q.toLowerCase()))) return false;
      if (statusF && statusCol && r[statusCol] !== statusF) return false;
      return true;
    });
    return applySort(arr, sort);
  }, [rows, q, statusF, sort, statusCol]);

  const pageCount = Math.max(1, Math.ceil(filtered.length / PAGE));
  const pageRows = filtered.slice((page - 1) * PAGE, page * PAGE);

  const creatorCol = headers.find((h) => /creator|name|username|handle/i.test(h));
  const profileCol = headers.find((h) => /profile.*url|insta|url/i.test(h));

  if (!rows.length) return <EmptyState title="No data yet." subtitle="Once your sheet is populated this table will fill in automatically." />;

  return (
    <div data-testid="creators-tab">
      <div className="flex items-center justify-between mb-5">
        <div>
          <h2 className="font-display text-xl font-semibold">Creator performance</h2>
          <p className="text-sm text-[var(--text-secondary)] mt-0.5">{filtered.length} creator{filtered.length === 1 ? "" : "s"} in this campaign</p>
        </div>
        <div className="flex items-center gap-2">
          <div className="relative">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--text-secondary)]" />
            <input className="wl-input pl-8 w-64" placeholder="Search creators" value={q} onChange={(e) => setQ(e.target.value)} data-testid="creators-search" />
          </div>
          {statusOptions.length > 0 && (
            <select className="wl-input w-auto" value={statusF} onChange={(e) => setStatusF(e.target.value)} data-testid="creators-filter-status">
              <option value="">Status: All</option>
              {statusOptions.map((s) => <option key={s} value={s}>{resolveStatus(s, statusMappings)?.label || s}</option>)}
            </select>
          )}
          <ClearFiltersButton
            filters={{ q, statusF, sortKey: sort.key }}
            defaults={{ q: "", statusF: "", sortKey: null }}
            onClear={() => { setQ(""); setStatusF(""); setSort({ key: null, dir: null, numeric: false }); setPage(1); }}
            testid="creators-clear-filters"
          />
        </div>
      </div>
      <div className="wl-card-solid overflow-hidden">
        <table className="wl-table">
          <thead>
            <tr>
              {columns.map((c) => {
                const numeric = !!numericMap[c.column];
                const align = numeric ? "right" : (c.align || "left");
                return <SortableTh key={c.column} label={c.display_name} sortKey={c.column} sort={sort} setSort={setSort} numeric={numeric} align={align} />;
              })}
            </tr>
          </thead>
          <tbody>
            {pageRows.map((r, i) => (
              <tr key={i} data-testid={`creator-row-${i}`}>
                {columns.map((c, j) => {
                  const v = r[c.column];
                  const numeric = !!numericMap[c.column];
                  const align = numeric ? "right" : (c.align || "left");
                  let content;
                  if (j === 0 && creatorCol === c.column) {
                    const profile = profileCol ? r[profileCol] : null;
                    const handle = profile ? (profile.split("/").filter(Boolean).pop() || "") : "";
                    const platform = detectPlatform(profile);
                    content = (
                      <div className="flex items-center gap-3">
                        <Avatar name={v} size={32} />
                        <div className="min-w-0">
                          <div className="font-medium leading-tight truncate">{v || "—"}</div>
                          {profile && (
                            <a href={profile} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-xs text-[var(--text-secondary)] hover:text-[var(--brand)] mt-0.5">
                              <PlatformIcon platform={platform} size={11} />@{handle}
                            </a>
                          )}
                        </div>
                      </div>
                    );
                  } else if (/status|opt[\s\-_]?in/i.test(c.column)) {
                    content = <StatusPill value={v} mappings={statusMappings} />;
                  } else if (/url|link|profile|insta/i.test(c.column) && v && /^https?:\/\//.test(String(v))) {
                    const platform = detectPlatform(v);
                    content = (
                      <a href={v} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 text-[13px] text-[var(--text-primary)] hover:text-[var(--brand)] transition-colors group" title={v} data-testid={`creator-link-${i}`}>
                        <span className="inline-flex items-center justify-center w-6 h-6 rounded-md bg-white border border-[var(--border-default)] shrink-0">
                          <PlatformIcon platform={platform} size={12} />
                        </span>
                        <span className="font-medium capitalize">{platform === "web" ? "Open" : platform}</span>
                        <ExternalLink size={11} className="opacity-50 group-hover:opacity-100" />
                      </a>
                    );
                  } else if (numeric) {
                    content = <span className="font-medium tabular-nums">{formatByType(v, c.format) || "—"}</span>;
                  } else {
                    content = formatByType(v, c.format);
                  }
                  return <td key={c.column} style={{ textAlign: align }}>{content}</td>;
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="mt-4 flex justify-end"><Pagination page={page} pageCount={pageCount} onChange={setPage} /></div>
    </div>
  );
}

function ContentTab({ cfg, rows, headers, statusMappings }) {
  const [page, setPage] = useState(1);
  const [q, setQ] = useState("");
  const [statusF, setStatusF] = useState("");
  const [sort, setSort] = useState({ key: null, dir: null, numeric: false });
  const PAGE = 10;

  const columns = useMemo(() => {
    const cols = (cfg?.columns || []).filter((c) => c.visible).sort((a, b) => (a.order || 0) - (b.order || 0));
    if (cols.length) return cols;
    return headers.map((h) => ({ column: h, display_name: h, format: "text", align: "left", visible: true }));
  }, [cfg, headers]);

  const numericMap = useMemo(() => {
    const m = {};
    columns.forEach((c) => { m[c.column] = ["number", "currency", "percentage"].includes(c.format) || isNumericColumn(rows, c.column); });
    return m;
  }, [columns, rows]);

  const statusCol = headers.find((h) => /status/i.test(h));
  const statusOptions = useMemo(() => statusCol ? [...new Set(rows.map((r) => r[statusCol]).filter(Boolean))] : [], [rows, statusCol]);

  const filtered = useMemo(() => {
    const arr = rows.filter((r) => {
      if (q && !Object.values(r).some((v) => String(v).toLowerCase().includes(q.toLowerCase()))) return false;
      if (statusF && statusCol && r[statusCol] !== statusF) return false;
      return true;
    });
    return applySort(arr, sort);
  }, [rows, q, statusF, sort, statusCol]);
  const pageCount = Math.max(1, Math.ceil(filtered.length / PAGE));
  const pageRows = filtered.slice((page - 1) * PAGE, page * PAGE);

  const creatorCol = headers.find((h) => /creator|username|handle/i.test(h));
  const urlCol = headers.find((h) => /url|link|post/i.test(h));

  if (!rows.length) return <EmptyState title="No data yet." subtitle="Once your sheet is populated this table will fill in automatically." />;

  const totalCreators = creatorCol ? new Set(rows.map((r) => r[creatorCol])).size : 0;

  return (
    <div data-testid="content-tab">
      <div className="flex items-center justify-between mb-5">
        <div>
          <h2 className="font-display text-xl font-semibold">Content performance</h2>
          <p className="text-sm text-[var(--text-secondary)] mt-0.5">{filtered.length} post{filtered.length === 1 ? "" : "s"}{totalCreators ? ` from ${totalCreators} creator${totalCreators === 1 ? "" : "s"}` : ""}</p>
        </div>
        <div className="flex items-center gap-2">
          <div className="relative">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--text-secondary)]" />
            <input className="wl-input pl-8 w-64" placeholder="Search posts or creators" value={q} onChange={(e) => setQ(e.target.value)} data-testid="content-search" />
          </div>
          {statusOptions.length > 0 && (
            <select className="wl-input w-auto" value={statusF} onChange={(e) => setStatusF(e.target.value)} data-testid="content-filter-status">
              <option value="">Status: All</option>
              {statusOptions.map((s) => <option key={s} value={s}>{resolveStatus(s, statusMappings)?.label || s}</option>)}
            </select>
          )}
          <ClearFiltersButton
            filters={{ q, statusF, sortKey: sort.key }}
            defaults={{ q: "", statusF: "", sortKey: null }}
            onClear={() => { setQ(""); setStatusF(""); setSort({ key: null, dir: null, numeric: false }); setPage(1); }}
            testid="content-clear-filters"
          />
        </div>
      </div>
      <div className="wl-card-solid overflow-hidden">
        <table className="wl-table">
          <thead>
            <tr>
              {columns.map((c) => {
                const numeric = !!numericMap[c.column];
                const align = numeric ? "right" : (c.align || "left");
                return <SortableTh key={c.column} label={c.display_name} sortKey={c.column} sort={sort} setSort={setSort} numeric={numeric} align={align} />;
              })}
            </tr>
          </thead>
          <tbody>
            {pageRows.map((r, i) => (
              <tr key={i} data-testid={`content-row-${i}`}>
                {columns.map((c, j) => {
                  const v = r[c.column];
                  const numeric = !!numericMap[c.column];
                  const align = numeric ? "right" : (c.align || "left");
                  let content;
                  if (j === 0 && creatorCol === c.column) {
                    content = <div className="flex items-center gap-3"><Avatar name={v} size={30} /><span className="font-medium">{v || "—"}</span></div>;
                  } else if (/status/i.test(c.column)) {
                    content = <StatusPill value={v} mappings={statusMappings} />;
                  } else if (c.column === urlCol && v) {
                    const platform = detectPlatform(v);
                    content = (
                      <a href={v} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 text-[13px] text-[var(--text-primary)] hover:text-[var(--brand)] transition-colors group" title={v}>
                        <span className="inline-flex items-center justify-center w-6 h-6 rounded-md bg-white border border-[var(--border-default)] shrink-0">
                          <PlatformIcon platform={platform} size={12} />
                        </span>
                        <span className="font-medium capitalize">{platform === "web" ? "Open" : platform}</span>
                        <ExternalLink size={11} className="opacity-50 group-hover:opacity-100" />
                      </a>
                    );
                  } else if (numeric) {
                    const fmt = c.format && c.format !== "text" ? c.format : (toNumber(v) >= 1000 ? "number" : "text");
                    content = <span className="font-medium tabular-nums">{formatByType(v, fmt) || "—"}</span>;
                  } else {
                    content = formatByType(v, c.format);
                  }
                  return <td key={c.column} style={{ textAlign: align }}>{content}</td>;
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="mt-4 flex justify-end"><Pagination page={page} pageCount={pageCount} onChange={setPage} /></div>
    </div>
  );
}
